import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { connectProjectToSync, setProjectPocOwner, setManualPlannedSessions, getProjectById, getProjectMilestones, requestMilestoneCompletion, getProjectCompetitionActivity } from '../../services/projectService';
import { getPocOwners } from '../../services/pocOwnerService';
import { getClosureRecord, createClosureRecord, updateClosureRecord } from '../../services/closureService';
import PocCallCard from './PocCallCard';
import { getClassesByProject } from '../../services/classService';
import { getAssignmentsByProject } from '../../services/assignmentService';
import useAuth from '../../hooks/useAuth';
import { useToast, useConfirm } from '../../context/ToastContext';
import { formatDate } from '../../utils/helpers';
import { getRecommendedClasses } from '../../utils/dataRecommendedClasses';
import styles from './ProjectDetails.module.css';

// The furthest a recommendation got. Two separate decisions land on one row —
// the reviewer's tick/cross and the student's activation — so the label reads
// the student's state first and falls back to the review state.
const compReviewLabel = (item) => {
  if (item.state === 'SELECTED') {
    return <span className={styles.compChosen}>Activated by student</span>;
  }
  if (item.review_state === 'APPROVED') return <span>Shortlisted</span>;
  if (item.review_state === 'REJECTED') {
    return <span className={styles.compCrossed}>Crossed off</span>;
  }
  return <span className={styles.compMuted}>Awaiting review</span>;
};

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, isPocOwner } = useAuth();
  const isMentor = user?.role === 'mentor';
  const canManageSync = ['super_admin', 'manager'].includes(user?.role);
  const toast = useToast();
  const confirm = useConfirm();
  // The competition-activity section is reviewer-only: the rows carry the
  // model's score and reasoning, including for competitions the student was
  // never shown. Students reach this page too, and for them the endpoint 403s,
  // so it is not even requested.
  const canSeeCompetitionActivity = ['super_admin', 'manager', 'mentor', 'poc_owner']
    .includes(user?.role);
  const [project, setProject] = useState(null);
  const [compActivity, setCompActivity] = useState(null);
  let [milestones, setMilestones] = useState([]);
  const [classes, setClasses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignmentStatuses, setAssignmentStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncGroupId, setSyncGroupId] = useState('');
  const [groupJid, setGroupJid] = useState('');
  const [showGroupJid, setShowGroupJid] = useState(false);
  const [connectingSync, setConnectingSync] = useState(false);
  const [pocOwners, setPocOwners] = useState([]);
  const [selectedPocOwnerId, setSelectedPocOwnerId] = useState('');
  const [savingPocOwner, setSavingPocOwner] = useState(false);
  const [plannedSessionsOverride, setPlannedSessionsOverride] = useState('');
  const [savingPlannedSessions, setSavingPlannedSessions] = useState(false);
  const [closureRecord, setClosureRecord] = useState(null);
  const [creatingClosure, setCreatingClosure] = useState(false);
  const [savingClosure, setSavingClosure] = useState(false);
  const [closureForm, setClosureForm] = useState({
    closure_call_date: '',
    project_outcome: '',
    skill_survey_completed: false,
    mentor_feedback_survey_completed: false,
    program_feedback_survey_completed: false,
    recommended_next_programs: '',
    recommended_competition_or_research_pathway: '',
    high_potential_flag: false,
    high_potential_call_completed: false,
    follow_up_date: '',
    conversion_status: '',
    notes: '',
  });

  const hydrateClosureForm = (record) => {
    setClosureForm({
      closure_call_date: record.closure_call_date || '',
      project_outcome: record.project_outcome || '',
      skill_survey_completed: !!record.skill_survey_completed,
      mentor_feedback_survey_completed: !!record.mentor_feedback_survey_completed,
      program_feedback_survey_completed: !!record.program_feedback_survey_completed,
      recommended_next_programs: (record.recommended_next_programs || []).join(', '),
      recommended_competition_or_research_pathway: record.recommended_competition_or_research_pathway || '',
      high_potential_flag: !!record.high_potential_flag,
      high_potential_call_completed: !!record.high_potential_call_completed,
      follow_up_date: record.follow_up_date || '',
      conversion_status: record.conversion_status || '',
      notes: record.notes || '',
    });
  };

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const [projectData, milestonesData, classesData, assignmentsData] = await Promise.all([
          getProjectById(projectId),
          getProjectMilestones(projectId),
          getClassesByProject(projectId),
          getAssignmentsByProject(projectId)
        ]);

        setProject(projectData);
        setSyncGroupId(projectData.sync_group_id || '');
        setSelectedPocOwnerId(projectData.poc_owner_id || '');
        setPlannedSessionsOverride(projectData.planned_total_sessions ?? '');
        setMilestones(milestonesData);
        setClasses(classesData);
        setAssignments(assignmentsData);

        if (canManageSync) {
          try {
            setPocOwners(await getPocOwners());
          } catch (error) {
            console.error('Error fetching POC owners:', error);
          }
        }

        if (canSeeCompetitionActivity) {
          try {
            setCompActivity(await getProjectCompetitionActivity(projectId));
          } catch (error) {
            // A project with no matcher history, or a mentor/POC owner who does
            // not own this one, must not take the rest of the page down.
            console.error('Error fetching competition activity:', error);
          }
        }

        // Slice 11: closure records are POC-owner self-service only — admin/manager have no
        // access at all anymore (confirmed explicitly, not just downgraded to read-only).
        if (isPocOwner) {
          try {
            const record = await getClosureRecord(projectId);
            setClosureRecord(record);
            hydrateClosureForm(record);
          } catch (error) {
            if (error.response?.status !== 404) {
              console.error('Error fetching closure record:', error);
            }
            // 404 just means no closure record exists yet for this project — not an error.
            // 403 means this POC owner isn't the one assigned to this project — also fine to
            // stay silent, the card below simply won't render anything actionable for them.
          }
        }

        // Assignment status is now returned from backend with each assignment

        const statusMap = {};
        for (const assignment of assignmentsData) {
          statusMap[assignment.id] = assignment.submission_status || 'assigned';
        }
        setAssignmentStatuses(statusMap);
      } catch (error) {
        console.error('Error fetching project details:', error);
        toast.error('Error loading project details');
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const getClassStatus = (cls) => {
    const classDate = new Date(cls.class_date);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    classDate.setHours(0, 0, 0, 0);

    const isPast = classDate < currentDate;

    if (!isPast) {
      return { color: 'empty', icon: '○' };
    }

    if (cls.mentor_attendance && cls.student_attendance) {
      return { color: 'green', icon: 'Yes' };
    } else if (cls.mentor_attendance && !cls.student_attendance) {
      return { color: 'blue', icon: 'Blue' };
    } else if (!cls.mentor_attendance && cls.student_attendance) {
      return { color: 'orange', icon: 'Orange' };
    } else {
      return { color: 'grey', icon: 'Grey' };
    }
  };

  const getClassesForMilestoneItem = (milestoneId, itemIndex) => {
    return classes.filter(cls =>
      cls.milestone_id === milestoneId &&
      cls.milestone_item_index === itemIndex
    );
  };

  const getUnlinkedClasses = () => {
    return classes.filter(cls => !cls.milestone_id);
  };

  const getAssignmentsForClass = (classId) => {
    return assignments.filter(a => a.class_id === classId);
  };

  const getUnlinkedAssignments = () => {
    return assignments.filter(a => !a.class_id);
  };

  const handleMarkMilestoneComplete = async (milestone) => {
    // Check if project scope is approved
    if (!project.scope_approved) {
      toast.warning('Project scope must be approved before marking milestones complete');
      return;
    }

    const confirmed = await confirm(`Mark Milestone ${milestone.milestone_number} as complete and send for admin approval?`, { confirmText: 'Mark Complete', variant: 'warning' });
    if (confirmed) {
      try {
        await requestMilestoneCompletion(milestone.id);
        toast.success('Milestone marked as complete.');
        // Refresh project details
        window.location.reload();
      } catch (error) {
        toast.error('Error: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const canMarkComplete = (milestone) => {
    return isMentor &&
      project?.scope_approved &&
      (milestone.status === 'pending' || milestone.status === 'rejected');
  };

  const handleConnectSync = async () => {
    if (!syncGroupId.trim()) {
      toast.warning('Enter the SYNC group UUID');
      return;
    }

    setConnectingSync(true);
    try {
      const connectionData = { sync_group_id: syncGroupId.trim() };
      if (groupJid.trim()) connectionData.group_jid = groupJid.trim();
      const updatedProject = await connectProjectToSync(projectId, connectionData);
      setProject(updatedProject);
      setSyncGroupId(updatedProject.sync_group_id || syncGroupId.trim());
      toast.success('Project connected to SYNC');
    } catch (error) {
      const message = error.response?.data?.detail || 'Could not connect to SYNC';
      if (message.toLowerCase().includes('group_jid')) setShowGroupJid(true);
      toast.error(message);
    } finally {
      setConnectingSync(false);
    }
  };

  const handleSavePocOwner = async () => {
    setSavingPocOwner(true);
    try {
      const updatedProject = await setProjectPocOwner(projectId, selectedPocOwnerId || null);
      setProject(updatedProject);
      toast.success('POC owner updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update POC owner');
    } finally {
      setSavingPocOwner(false);
    }
  };

  const handleSavePlannedSessions = async () => {
    // Deliberately does not support clearing to null from this control — that would also wipe out
    // an auto-derived (non-override) value with no way to re-derive it short of re-approving the
    // scope. Overriding always requires a real positive number.
    const value = parseInt(plannedSessionsOverride, 10);
    if (!value || value <= 0) {
      toast.warning('Enter a positive number of sessions');
      return;
    }
    setSavingPlannedSessions(true);
    try {
      const updatedProject = await setManualPlannedSessions(projectId, value);
      setProject(updatedProject);
      toast.success('Planned sessions updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update planned sessions');
    } finally {
      setSavingPlannedSessions(false);
    }
  };

  const handleCreateClosureRecord = async () => {
    setCreatingClosure(true);
    try {
      const record = await createClosureRecord(projectId);
      setClosureRecord(record);
      hydrateClosureForm(record);
      toast.success('Closure record created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create closure record');
    } finally {
      setCreatingClosure(false);
    }
  };

  // Booking/cancelling the end-of-project call writes closure_call_date server-side.
  const refreshClosureRecord = async () => {
    try {
      const record = await getClosureRecord(projectId);
      setClosureRecord(record);
      hydrateClosureForm(record);
    } catch (error) {
      if (error.response?.status !== 404) console.error('Error refreshing closure record:', error);
    }
  };

  const handleClosureFieldChange = (field, value) => {
    setClosureForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveClosureRecord = async () => {
    setSavingClosure(true);
    try {
      const payload = {
        ...closureForm,
        closure_call_date: closureForm.closure_call_date || null,
        follow_up_date: closureForm.follow_up_date || null,
        conversion_status: closureForm.conversion_status || null,
        recommended_next_programs: closureForm.recommended_next_programs
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const updated = await updateClosureRecord(closureRecord.id, payload);
      setClosureRecord(updated);
      hydrateClosureForm(updated);
      toast.success('Closure record updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update closure record');
    } finally {
      setSavingClosure(false);
    }
  };

  const getMilestoneStatusDisplay = (milestone) => {
    const statusConfig = {
      'pending': { label: 'PENDING', className: 'milestoneStatusPending' },
      'awaiting_approval': { label: 'AWAITING APPROVAL', className: 'milestoneStatusAwaiting' },
      'approved': { label: 'APPROVED', className: 'milestoneStatusApproved' },
      'rejected': { label: 'REJECTED', className: 'milestoneStatusRejected' }
    };

    return statusConfig[milestone.status] || { label: milestone.status.toUpperCase(), className: 'milestoneStatusPending' };
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!project) {
    return (
      <div className={styles.error}>
        <h2>Project not found</h2>
        <Button onClick={() => navigate(-1)} loading={loading}>Go Back</Button>
      </div>
    );
  }
  const isProjectCompleted = project.status === 'completed';
  const displayMilestones = isProjectCompleted ? milestones : milestones.filter(milestone => milestone.status === "pending").slice(0, 1);
  
  return (
    <div className={styles.projectDetails}>
      <div className={styles.header}>
        <div>
          <h1>{project.project_name}</h1>
          <p className={styles.track}>Track: {project.project_track}</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)} loading={loading}>
          ← Back
        </Button>
      </div>

      <Card>
        <h2>Project Information</h2>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <strong>Status:</strong>
            <span className={styles[`status${project.status.charAt(0).toUpperCase() + project.status.slice(1).replace('_', '')}`]}>
              {project.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <div className={styles.infoItem}>
            <strong>Start Date:</strong>
            <span>{formatDate(project.start_date)}</span>
          </div>
          <div className={styles.infoItem}>
            <strong>End Date:</strong>
            <span>{project.end_date ? formatDate(project.end_date) : 'Not set'}</span>
          </div>
          <div className={styles.infoItem}>
            <strong>Scope Approved:</strong>
            <span>{project.scope_approved ? 'Yes' : 'No'}</span>
          </div>
        </div>
        {project.project_description && (
          <div className={styles.description}>
            <strong>Description:</strong>
            <p>{project.project_description}</p>
          </div>
        )}
      </Card>

      {canManageSync && (
        <div className={styles.syncSection}>
          <div className={styles.syncHeader}>
            <div>
              <h2>SYNC Connection</h2>
              <p>Connect this project to its SYNC WhatsApp group.</p>
            </div>
            <span className={project.sync_group_id ? styles.syncConnected : styles.syncNotConnected}>
              {project.sync_group_id ? 'Already linked' : 'Not connected'}
            </span>
          </div>
          {project.sync_group_id ? (
            <div className={styles.linkedGroup}>
              <span>SYNC Group UUID</span>
              <code>{project.sync_group_id}</code>
            </div>
          ) : (
            <div className={styles.syncForm}>
              <label htmlFor="sync-group-id">SYNC Group UUID</label>
              <div className={styles.syncInputRow}>
                <input
                  id="sync-group-id"
                  type="text"
                  value={syncGroupId}
                  onChange={(event) => setSyncGroupId(event.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
                <Button onClick={handleConnectSync} loading={connectingSync} disabled={!syncGroupId.trim()}>
                  Connect
                </Button>
              </div>
              {showGroupJid && (
                <div className={styles.jidField}>
                  <label htmlFor="sync-group-jid">WhatsApp Group JID</label>
                  <input
                    id="sync-group-jid"
                    type="text"
                    value={groupJid}
                    onChange={(event) => setGroupJid(event.target.value)}
                    placeholder="1234567890@g.us"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {canManageSync && (
        <Card>
          <h2>POC Owner (Upsell Loop)</h2>
          <div className={styles.syncInputRow}>
            <select
              value={selectedPocOwnerId}
              onChange={(event) => setSelectedPocOwnerId(event.target.value)}
            >
              <option value="">— Unassigned —</option>
              {pocOwners.map((owner) => (
                <option key={owner.id} value={owner.id}>{owner.name}</option>
              ))}
            </select>
            <Button
              onClick={handleSavePocOwner}
              loading={savingPocOwner}
              disabled={(selectedPocOwnerId || '') === (project.poc_owner_id || '')}
            >
              Save
            </Button>
          </div>
        </Card>
      )}

      {canManageSync && (
        <Card className={styles.plannedSessionsCard}>
          <h2>Planned Sessions (Upsell Loop)</h2>
          <div className={styles.plannedSessionsInfo}>
            <div className={styles.plannedSessionsInfoItem}>
              <strong>Current value:</strong>
              <span>{project.planned_total_sessions ?? 'Not set'}</span>
            </div>
            <div className={styles.plannedSessionsInfoItem}>
              <strong>Source:</strong>
              <span>{project.planned_sessions_source || 'none'}</span>
            </div>
          </div>
          <div className={styles.syncInputRow}>
            <input
              type="number"
              min="1"
              value={plannedSessionsOverride}
              onChange={(e) => setPlannedSessionsOverride(e.target.value)}
              placeholder="e.g. 12"
            />
            <Button onClick={handleSavePlannedSessions} loading={savingPlannedSessions}>
              Save Override
            </Button>
          </div>
        </Card>
      )}

      {isPocOwner && (
        <PocCallCard projectId={projectId} onEndCallChanged={refreshClosureRecord} />
      )}

      {isPocOwner && (
        <Card className={styles.closureCard}>
          <h2>Closure Record (Upsell Loop)</h2>
          {!closureRecord ? (
            <>
              <p>No closure record exists yet for this project.</p>
              <Button onClick={handleCreateClosureRecord} loading={creatingClosure}>
                Create Closure Record
              </Button>
            </>
          ) : (
            <>
              <div className={styles.closureMeta}>
                <div>
                  <strong>POC Owner at creation</strong>
                  <span>
                    {closureRecord.poc_owner_name
                      ? `${closureRecord.poc_owner_name} (${closureRecord.poc_owner_email})`
                      : 'Unassigned'}
                  </span>
                </div>
                <div>
                  <strong>Trigger</strong>
                  <span>{closureRecord.trigger_reason || 'manual'}</span>
                </div>
              </div>

              <div className={styles.closureForm}>
                <div className={styles.formSection}>
                  <h3 className={styles.formSectionTitle}>Closure Call</h3>
                  <div className={styles.formGroup}>
                    <label htmlFor="closure-call-date">Closure Call Date</label>
                    <input
                      id="closure-call-date"
                      type="date"
                      value={closureForm.closure_call_date || ''}
                      onChange={(e) => handleClosureFieldChange('closure_call_date', e.target.value)}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="project-outcome">Project Outcome</label>
                    <textarea
                      id="project-outcome"
                      value={closureForm.project_outcome}
                      onChange={(e) => handleClosureFieldChange('project_outcome', e.target.value)}
                      placeholder="How did the project/program go?"
                    />
                  </div>
                </div>

                <div className={styles.formSection}>
                  <h3 className={styles.formSectionTitle}>Surveys Completed</h3>
                  <div className={styles.checkboxRow}>
                    <input
                      id="skill-mentor-survey"
                      type="checkbox"
                      checked={closureForm.skill_survey_completed && closureForm.mentor_feedback_survey_completed}
                      onChange={(e) => {
                        handleClosureFieldChange('skill_survey_completed', e.target.checked);
                        handleClosureFieldChange('mentor_feedback_survey_completed', e.target.checked);
                      }}
                    />
                    <label htmlFor="skill-mentor-survey">Skill &amp; mentor feedback survey completed</label>
                  </div>
                  <div className={styles.checkboxRow}>
                    <input
                      id="program-feedback-survey"
                      type="checkbox"
                      checked={closureForm.program_feedback_survey_completed}
                      onChange={(e) => handleClosureFieldChange('program_feedback_survey_completed', e.target.checked)}
                    />
                    <label htmlFor="program-feedback-survey">Program feedback survey completed</label>
                  </div>
                </div>

                <div className={styles.formSection}>
                  <h3 className={styles.formSectionTitle}>Next Steps</h3>
                  <div className={styles.formGroup}>
                    <label htmlFor="next-programs">Recommended Next Program(s) (comma-separated)</label>
                    <input
                      id="next-programs"
                      type="text"
                      value={closureForm.recommended_next_programs}
                      onChange={(e) => handleClosureFieldChange('recommended_next_programs', e.target.value)}
                      placeholder="e.g. Competition Mastery, Research & Build"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="next-pathway">Recommended Competition/Research Pathway</label>
                    <input
                      id="next-pathway"
                      type="text"
                      value={closureForm.recommended_competition_or_research_pathway}
                      onChange={(e) => handleClosureFieldChange('recommended_competition_or_research_pathway', e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.formSection}>
                  <h3 className={styles.formSectionTitle}>High-Potential Follow-Up</h3>
                  <div className={styles.checkboxRow}>
                    <input
                      id="high-potential-flag"
                      type="checkbox"
                      checked={closureForm.high_potential_flag}
                      onChange={(e) => handleClosureFieldChange('high_potential_flag', e.target.checked)}
                    />
                    <label htmlFor="high-potential-flag">High-potential flag</label>
                  </div>
                  <div className={styles.checkboxRow}>
                    <input
                      id="high-potential-call-completed"
                      type="checkbox"
                      checked={closureForm.high_potential_call_completed}
                      onChange={(e) => handleClosureFieldChange('high_potential_call_completed', e.target.checked)}
                    />
                    <label htmlFor="high-potential-call-completed">Direct phone call completed</label>
                  </div>
                </div>

                <div className={styles.formSection}>
                  <h3 className={styles.formSectionTitle}>Follow-Up &amp; Outcome</h3>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="follow-up-date">Follow-up Date</label>
                      <input
                        id="follow-up-date"
                        type="date"
                        value={closureForm.follow_up_date || ''}
                        onChange={(e) => handleClosureFieldChange('follow_up_date', e.target.value)}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="conversion-status">Conversion Status</label>
                      <select
                        id="conversion-status"
                        value={closureForm.conversion_status}
                        onChange={(e) => handleClosureFieldChange('conversion_status', e.target.value)}
                      >
                        <option value="">— Not set —</option>
                        <option value="converted">Converted</option>
                        <option value="follow_up_later">Follow-up Later</option>
                        <option value="not_interested">Not Interested</option>
                        <option value="no_response">No Response</option>
                      </select>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="closure-notes">Notes</label>
                    <textarea
                      id="closure-notes"
                      value={closureForm.notes}
                      onChange={(e) => handleClosureFieldChange('notes', e.target.value)}
                    />
                  </div>
                </div>

                <Button
                  className={styles.closureSaveBtn}
                  onClick={handleSaveClosureRecord}
                  loading={savingClosure}
                  size="large"
                >
                  Save Closure Record
                </Button>
              </div>
            </>
          )}
        </Card>
      )}

      {/* Competitions: what the matcher proposed, and who acted on it. Three
          tables rather than one with a "source" column — they answer different
          questions and carry genuinely different columns (a match has a score
          and a rank; a review decision has who ticked it and when). */}
      {canSeeCompetitionActivity && compActivity && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>Competitions</h2>
          </div>

          <Card className={styles.compCard}>
            <h3 className={styles.compHeading}>
              Matched by the system
              <span className={styles.compCount}>{compActivity.matched.length}</span>
            </h3>
            {compActivity.matched.length === 0 ? (
              <p className={styles.noData}>No matcher run has been done for this project yet.</p>
            ) : (
              <div className={styles.compTableWrap}>
                <table className={styles.compTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Competition</th>
                      <th>Fit</th>
                      <th>Score</th>
                      <th>Deadline</th>
                      <th>Run</th>
                      <th>State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compActivity.matched.map((m) => (
                      <tr key={m.item_id}>
                        <td>{m.rank}</td>
                        <td>
                          <div className={styles.compName}>{m.name}</div>
                          {m.reason && <div className={styles.compReason}>{m.reason}</div>}
                        </td>
                        <td>{m.fit_bucket || '—'}</td>
                        <td>{m.score ?? '—'}</td>
                        <td>{m.deadline ? formatDate(m.deadline) : '—'}</td>
                        <td className={styles.compMuted}>
                          {formatDate(m.run_created_at)}<br />{m.run_status}
                        </td>
                        <td>{compReviewLabel(m)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className={styles.compCard}>
            <h3 className={styles.compHeading}>
              Shortlisted by admin / POC owner
              <span className={styles.compCount}>{compActivity.poc_selected.length}</span>
              {compActivity.rejected.length > 0 && (
                <span className={styles.compSubnote}>
                  {compActivity.rejected.length} crossed off
                </span>
              )}
            </h3>
            {compActivity.poc_selected.length === 0 ? (
              <p className={styles.noData}>
                Nothing has been ticked in review yet — until a competition is ticked, the
                student does not see it.
              </p>
            ) : (
              <div className={styles.compTableWrap}>
                <table className={styles.compTable}>
                  <thead>
                    <tr>
                      <th>Competition</th>
                      <th>Fit</th>
                      <th>Deadline</th>
                      <th>Ticked on</th>
                      <th>Ticked by</th>
                      <th>Student</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compActivity.poc_selected.map((p) => (
                      <tr key={p.item_id}>
                        <td>
                          <div className={styles.compName}>{p.name}</div>
                          {p.review_note && (
                            <div className={styles.compReason}>Note: {p.review_note}</div>
                          )}
                        </td>
                        <td>{p.fit_bucket || '—'}</td>
                        <td>{p.deadline ? formatDate(p.deadline) : '—'}</td>
                        <td>{p.reviewed_at ? formatDate(p.reviewed_at) : '—'}</td>
                        <td className={styles.compMuted}>
                          {p.reviewed_by_email || '—'}
                          {p.reviewed_by_role ? ` (${p.reviewed_by_role.replace(/_/g, ' ')})` : ''}
                        </td>
                        <td>
                          {p.state === 'SELECTED'
                            ? <span className={styles.compChosen}>Activated</span>
                            : <span className={styles.compMuted}>Not taken up</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Crossed-off competitions. Folded away because the useful question
              is usually "what did we put forward"; this is here so a gap in the
              shortlist can be explained rather than guessed at. */}
          {compActivity.rejected.length > 0 && (
            <Card className={styles.compCard}>
              <details>
                <summary className={styles.compSummary}>
                  Crossed off in review ({compActivity.rejected.length})
                </summary>
                <div className={styles.compTableWrap}>
                  <table className={styles.compTable}>
                    <thead>
                      <tr>
                        <th>Competition</th>
                        <th>Fit</th>
                        <th>Crossed on</th>
                        <th>Crossed by</th>
                        <th>Reason given</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compActivity.rejected.map((x) => (
                        <tr key={x.item_id}>
                          <td><div className={styles.compName}>{x.name}</div></td>
                          <td>{x.fit_bucket || '—'}</td>
                          <td>{x.reviewed_at ? formatDate(x.reviewed_at) : '—'}</td>
                          <td className={styles.compMuted}>{x.reviewed_by_email || '—'}</td>
                          <td className={styles.compMuted}>{x.review_note || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </Card>
          )}

          <Card className={styles.compCard}>
            <h3 className={styles.compHeading}>
              Selected by the student
              <span className={styles.compCount}>{compActivity.student_selected.length}</span>
            </h3>
            {compActivity.student_selected.length === 0 ? (
              <p className={styles.noData}>The student has not picked anything from their report yet.</p>
            ) : (
              <div className={styles.compTableWrap}>
                <table className={styles.compTable}>
                  <thead>
                    <tr>
                      <th>Competition</th>
                      <th>Fit</th>
                      <th>Deadline</th>
                      <th>Chosen on</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compActivity.student_selected.map((s) => (
                      <tr key={s.item_id}>
                        <td><div className={styles.compName}>{s.name}</div></td>
                        <td>{s.fit_bucket || '—'}</td>
                        <td>{s.deadline ? formatDate(s.deadline) : '—'}</td>
                        <td>{s.selected_at ? formatDate(s.selected_at) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Milestones</h2>
          {isProjectCompleted && (
            <span className={styles.completedBadge} style={{ marginLeft: '12px', padding: '4px 8px', background: '#4CAF50', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
              ✓ PROJECT COMPLETED
            </span>
          )}
        </div>
        
        {displayMilestones.length === 0 ? (
          <p className={styles.noData}>No milestones found for this project.</p>
        ) : (
          displayMilestones.map(milestone => {
            const statusDisplay = getMilestoneStatusDisplay(milestone);

            return (
              <Card key={milestone.id} className={styles.milestoneCard}>
                <div className={styles.milestoneHeader}>
                  <div className={styles.milestoneTitle}>
                    <h3>Milestone {milestone.milestone_number}: {milestone.milestone_name}</h3>
                    {isMentor && canMarkComplete(milestone) && (
                      <Button size="small"
                        onClick={() => handleMarkMilestoneComplete(milestone)} loading={loading}
                        title={!project.scope_approved ? "Project scope must be approved first" : "Mark milestone as complete"}
                        disabled={!project.scope_approved}
                        style={{ marginLeft: '12px' }}
                      >
                        Mark Complete
                      </Button>
                    )}
                  </div>
                  <span className={styles[statusDisplay.className]}>
                    {statusDisplay.label}
                  </span>
                </div>

                {milestone.admin_feedback && milestone.status === 'rejected' && (
                  <div className={styles.rejectionFeedback}>
                    <strong>Admin Feedback:</strong>
                    <p>{milestone.admin_feedback}</p>
                  </div>
                )}

                {milestone.milestone_items && milestone.milestone_items.length > 0 ? (
                  <div className={styles.itemsList}>
                    {milestone.milestone_items.map((item, index) => {
                      const mappedClasses = getClassesForMilestoneItem(milestone.id, index);
                      const recommendedClasses = project?.project_track ? getRecommendedClasses(project.project_track, item) : null;

                      return (
                        <div key={index} className={styles.milestoneItem}>
                          <div className={styles.itemName}>
                            <span className={styles.itemBullet}>•</span>
                            <strong>
                              {item}
                              {recommendedClasses !== null && (
                                <span className={styles.recommendedClasses}>
                                  {' '}(Recommended number of Classes: {recommendedClasses})
                                </span>
                              )}
                            </strong>
                          </div>

                          {mappedClasses.length > 0 && (
                            <div className={styles.mappedClasses}>
                              <p className={styles.mappedLabel}>Mapped Classes:</p>
                              <ul className={styles.classList}>
                                {mappedClasses.map(cls => {
                                  const status = getClassStatus(cls);
                                  return (
                                    <li key={cls.id} className={styles.classItem}>
                                      <span className={styles.classIcon}>{status.icon}</span>
                                      <span>Class #{cls.class_number}</span>
                                      <span className={styles.classDate}>{formatDate(cls.class_date)}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={styles.noItems}>No items in this milestone</p>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Unlinked Classes Section */}
      {getUnlinkedClasses().length > 0 && (
        <div className={styles.unlinkedSection}>
          <h2>Unlinked Classes</h2>
          <Card>
            <ul className={styles.unlinkedList}>
              {getUnlinkedClasses().map(cls => {
                const status = getClassStatus(cls);
                return (
                  <li key={cls.id} className={styles.unlinkedItem}>
                    <span className={styles.classIcon}>{status.icon}</span>
                    <span><strong>Class #{cls.class_number}</strong></span>
                    <span className={styles.classDate}>{formatDate(cls.class_date)} at {cls.class_time}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
      )}

      {/* Assignments Section */}
      {assignments.length > 0 && (
        <div className={styles.assignmentsSection}>
          <h2>Assignments</h2>

          {/* Assignments grouped by class */}
          {classes.filter(cls => getAssignmentsForClass(cls.id).length > 0).map(cls => (
            <Card key={cls.id} className={styles.assignmentGroupCard}>
              <h4 className={styles.classGroupTitle}>
                Class #{cls.class_number} - {formatDate(cls.class_date)}
              </h4>
              <ul className={styles.assignmentsList}>
                {getAssignmentsForClass(cls.id).map(assignment => (
                  <li key={assignment.id} className={styles.assignmentItem}>
                    <span className={styles.assignmentTitle}>{assignment.title}</span>
                    <StatusBadge status={assignmentStatuses[assignment.id] || 'assigned'} />
                  </li>
                ))}
              </ul>
            </Card>
          ))}

          {/* Unlinked Assignments */}
          {getUnlinkedAssignments().length > 0 && (
            <Card className={styles.unlinkedAssignmentsCard}>
              <h4 className={styles.unlinkedAssignmentsTitle}>Unlinked Assignments</h4>
              <ul className={styles.assignmentsList}>
                {getUnlinkedAssignments().map(assignment => (
                  <li key={assignment.id} className={styles.assignmentItem}>
                    <span className={styles.assignmentTitle}>{assignment.title}</span>
                    <StatusBadge status={assignmentStatuses[assignment.id] || 'assigned'} />
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
